import { marked } from 'marked';
import DOMPurify from 'dompurify';
export function processExhibitionsData(floors, themeGroups) {
  const exhibitionsObject = {};
  floors.forEach((floor) => {
    filterOutNonCollectionFauxMDEs(floor);
    addPageLinksToExhibitions(floor.DirectoryData);
    const { amenities, exhibitionsWithoutAmenities } = sortAmenitiesFromExhibitions(floor.DirectoryData);
    const { themeGroupId, exhibitionsWithoutThemeGroups } = filterOutThemeGroupFromExhibitions(exhibitionsWithoutAmenities);
    const floorDataObject = {};
    floorDataObject.exhibitions = exhibitionsWithoutThemeGroups;
    floorDataObject.themeGroup = themeGroups.find((themeGroup) => themeGroup.Id === themeGroupId);
    floorDataObject.amenities = amenities;
    exhibitionsObject[floor.DefaultFloor] = floorDataObject;
  });

  const filteredData = filterLowerLevelFromFloorOne(exhibitionsObject['1']);
  exhibitionsObject['LL'] = filteredData.lowerLevel;
  exhibitionsObject['1'] = filteredData.floorOne;

  sortFloors(exhibitionsObject);

  addHighlightIds(exhibitionsObject);

  return exhibitionsObject;
}

function addHighlightIds(floors) {
  Object.keys(floors).forEach((floorKey) => {
    floors[floorKey].exhibitions.forEach((exhibition) => {
      exhibition.highlightID = formatToSVGId(floorKey, exhibition.LocationName);
    });
    floors[floorKey].themeGroup?.themes?.forEach((theme) => {
      theme.highlightID = formatToSVGId(floorKey, theme.title);
    });
  });
}

function filterOutThemeGroupFromExhibitions(exhibitions) {
  let themeGroupId = null;
  const exhibitionsWithoutThemeGroups = [];
  exhibitions.forEach((exhibition) => {
    if (exhibition.ThemeGroupId) {
      themeGroupId = exhibition.ThemeGroupId;
    } else {
      exhibitionsWithoutThemeGroups.push(exhibition);
    }
  });
  return { themeGroupId, exhibitionsWithoutThemeGroups };
}

function sortAmenitiesFromExhibitions(directoryData) {
  const amenities = [];
  const exhibitionsWithoutAmenities = [];
  directoryData.forEach((exhibition) => {
    if (exhibition.Type === 'Screens::Shows::Amenity') {
      amenities.push(exhibition);
    } else {
      exhibitionsWithoutAmenities.push(exhibition);
    }
  });
  return { amenities, exhibitionsWithoutAmenities };
}

function addPageLinksToExhibitions(directoryData) {
  directoryData.forEach((exhibition) => { exhibition.linkToPage = generateLinkFor(exhibition); });
}

function filterLowerLevelFromFloorOne(floorData) {
  const lowerLevel = {
    exhibitions: [],
    amenities: [],
  };
  const floorOne = {
    exhibitions: [],
    amenities: [],
  };
  floorData.exhibitions.forEach((exhibition) => {
    const dupArtLab = exhibition.Type === 'mde' && exhibition.Title.includes('Art Lab');
    if (exhibition.Floor === '1' && !dupArtLab) {
      floorOne.exhibitions.push(exhibition);
    } else {
      if (!dupArtLab) {
        lowerLevel.exhibitions.push(exhibition);
      }
    }
  });
  floorData.amenities.forEach((amenity) => {
    if (amenity.Floor === '1') {
      floorOne.amenities.push(amenity);
    } else {
      lowerLevel.amenities.push(amenity);
    }
  });
  return { floorOne, lowerLevel };
}

function filterOutNonCollectionFauxMDEs(floor) {
  floor.DirectoryData = floor.DirectoryData.filter((exhibition) => {
    return exhibition.Type !== 'Screens::Shows::FauxMDE' || exhibitionIsCollection(exhibition);
  });
}

function exhibitionIsCollection(exhibition) {
  return !!exhibition.ThemeGroupId;
}

function generateLinkFor(exhibition) {
  if (exhibition.Data.amenity_icon?.includes('artLab')) {
    return LINKS.artLab;
  }
  if (exhibition.Data.amenity_icon?.includes('store')) {
    return LINKS.store;
  }
  if (exhibition.Data.amenity_icon?.includes('restaurant')) {
    return LINKS.restaurant;
  }
  if (exhibition.ThemeGroupId) {
    return LINKS.themeGroup(exhibition);
  }
  if (exhibition.Type === 'mde') {
    return LINKS.mde(exhibition);
  }
  return null;
}

function sortFloors(floors) {
  Object.keys(floors).forEach((floorKey) => {
    if (floorKey !== "") floors[floorKey].exhibitions = sortFloorUsingFloorSortingKey(floorKey, floors);
  });
}

function sortFloorUsingFloorSortingKey(floorKey, floors) {
  return floors[floorKey].exhibitions.sort((a, b) => {
    const priority = (item) => {
      const lowestPriority = FloorSortingKey[floorKey].length;
      const keyIndex = FloorSortingKey[floorKey].findIndex((key) => item.LocationName.includes(key));

      return keyIndex !== -1 ? keyIndex : lowestPriority;
    };

    return priority(a) - priority(b);
  });
}

const LINKS = {
  artLab: '/calendar/exhibitions/5144',
  store: 'https://store.moma.org/?utm_source=moma.org',
  restaurant: '/visit/eating',
  mde: (exhibition) => `/calendar/exhibitions/${exhibition.Id}`,
  themeGroup: (exhibition) => `/calendar/floors/${exhibition.ThemeGroupId}`,
};

const FloorSortingKey = {
  1: ["North", "South", "Sculpture Garden", "The Modern Window", "Floor"],
  2: ["South", "Creativity Lab", "Floor"],
  3: ["North", "South", "East"],
  4: ["Studio", "Floor"],
  5: ["Floor"],
  6: ["Floor"],
  LL: ["Floor"],
};

/**
 * 
 * @param {string} floor - current map floor
 * @param {string} locationString - location of the object on the map
 * @returns corresponding id of svg element that corresponds to some floor object i.e exhibition, amenity, etc
 */
export function formatToSVGId(floor, locationString) {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = locationString;
  const parsedString = textarea.value.replace(/\s+/g, '');
  return `${floor}_${parsedString}`;
}

export function getExhibitionStatus(exhibition) {
  let status = '';
  if (exhibition.IsLastChance) {
    status = 'Last chance';
  } else if (exhibition.MdeStatus) {
    status = exhibition.MdeStatus;
  }
  return status;
}

/**
 * Fucntion extracts and formats data to be displayed in the overlay
 *
 * @param {object} exhibition - object containing exhibition data
 * @returns object containing data to be displayed in the overlay
 */
export function getExhibitionOverlayData(exhibition) {
  const throughDate = exhibition.DateRange.includes('Ongoing') ? 'Ongoing' : exhibition.DateRange;

  let mdeType = 'exhibition';
  if (exhibition.OriginalType === 'Calendar::MultiDayEvents::OtherOrInstallation') {
    mdeType = 'installation';
  } else if (exhibition.OriginalType === 'Calendar::MultiDayEvents::OtherProject') {
    mdeType = 'project';
  }

  return {
    location: exhibition.LocationName,
    title: getExhibitionTitle(exhibition),
    status: getExhibitionStatus(exhibition),
    throughDate,
    description: null,
    imageURL: exhibition.Asset?.URL,
    mdeLink: exhibition.linkToPage,
    linkText: `Learn more about this ${mdeType}`
  };
}

function getExhibitionTitle(exhibition) {
  if (exhibition.Type === 'mde' && exhibition.Data.Subtitle) {
    return `${exhibition.Title}: ${exhibition.Data.Subtitle}`;
  } else {
    return exhibition.Title;
  }
}

export function getGalleryStatus(gallery) {
  let status = '';
  if (gallery.IsMemberPreview) {
    status = 'Member previews';
  } else if (gallery.NewOnViewWorks) {
    status = 'New on view';
  } else if (gallery.IsLastChance) {
    status = 'Last chance';
  } else if (gallery.IsMemberLastLook) {
    status = 'Member last looks';
  }
  return status;
}

export function getGalleryOverlayData(gallery) {
  const themeSubtitleHTML = marked(gallery.subtitle);
  const themeSubtitle = DOMPurify.sanitize(themeSubtitleHTML);

  return {
    id: gallery.id,
    location: `Gallery ${gallery.title}`,
    title: themeSubtitle,
    status: getGalleryStatus(gallery),
    throughDate: null,
    description: gallery.short_description,
    imageURL: gallery.Asset?.URL,
    mdeLink: `/calendar/galleries/${gallery.id}`,
    linkText: 'Learn more about this gallery',
    isGallery: true
  };
}

export const ArtRoomsByFloor = {
  1: ["1_1South", "1_SculptureGarden", "1_TheModernWindow", "1_GardenLobby", "1_1North"],
  2: ["2_2South", "2_CreativityLab", "2_201", "2_202", "2_203", "2_204", "2_205", "2_206", "2_207", "2_208", "2_209", "2_210", "2_211", "2_212", "2_213", "2_214", "2_215", "2_216",],
  3: ["3_3North", "3_3South", "3_3East"],
  4: ["4_Studio", "4_400", "4_401", "4_402", "4_403", "4_404", "4_405", "4_406", "4_407", "4_408", "4_409", "4_410", "4_411", "4_412", "4_413", "4_414", "4_415", "4_416", "4_417", "4_418", "4_419", "4_420", "4_421"],
  5: ["5_500", "5_501", "5_502", "5_503", "5_504", "5_505", "5_506", "5_507", "5_508", "5_509", "5_510", "5_511", "5_512", "5_513", "5_514", "5_515", "5_516", "5_517", "5_518", "5_519", "5_520", "5_521", "5_522", "5_523"],
  6: ["6_Floor6"],
  LL: ["LL_FloorT2/T1", "LL_Atrium"]
};